import { apiFetch } from "./client";
import type { Booking, BookingPatch } from "./bookings";

export type { Booking, BookingPatch };

export type VerlaufEntry = {
  id: number;
  reservierung_id: number;
  zeitpunkt: string;
  aktion: string;
  details: string | null;
  akteur: string | null;
};

export type DokumentArt = "buchungsbestaetigung" | "infos_gruppe" | "rechnung" | "konus_meldung" | "meldeschein" | "sonstiges";

export type AblageFile = {
  name: string;
  path: string;
  groesse: number;
  dokument_art: DokumentArt;
  erstellt_am: string | null;
};

import type { WorkflowStatus } from "./constants";

export const ALLOWED_TRANSITIONS: Record<WorkflowStatus, WorkflowStatus[]> = {
  "Angelegt": ["Bestätigt", "Storniert", "Intern"],
  "Bestätigt": ["Konus Link versandt", "Storniert", "Intern"],
  "Konus Link versandt": ["Konus-Daten eingegeben", "Storniert", "Intern"],
  "Konus-Daten eingegeben": ["Infos an Gruppe versandt", "Storniert", "Intern"],
  "Infos an Gruppe versandt": ["Abgereist", "Storniert", "Intern"],
  "Abgereist": ["Rechnung erstellt", "Storniert", "Intern"],
  "Rechnung erstellt": ["Bezahlt", "Storniert", "Intern"],
  "Bezahlt": [],
  "Storniert": [],
  "Intern": [],
};

export function fetchBookingDetail(id: number): Promise<Booking> {
  return apiFetch<Booking>(`/api/bookings/${id}`);
}

export function fetchVerlauf(id: number): Promise<VerlaufEntry[]> {
  return apiFetch<VerlaufEntry[]>(`/api/bookings/${id}/verlauf`);
}

export function fetchAblage(id: number): Promise<AblageFile[]> {
  return apiFetch<AblageFile[]>(`/api/bookings/${id}/ablage`);
}

export function deleteBooking(id: number): Promise<{ detail: string }> {
  return apiFetch(`/api/bookings/${id}`, { method: "DELETE" });
}

export function postTransition(id: number, target: string, storniert_am?: string): Promise<Booking> {
  return apiFetch<Booking>(`/api/bookings/${id}/transitions/${encodeURIComponent(target)}`, {
    method: "POST",
    body: storniert_am !== undefined ? { storniert_am } : undefined,
  });
}

export function postDocument(
  id: number,
  art: string,
  opts: { storno_betrag?: number } = {},
): Promise<{ detail: string; path?: string; paths?: string[] }> {
  const qs = new URLSearchParams();
  if (opts.storno_betrag !== undefined) qs.set("storno_betrag", String(opts.storno_betrag));
  const suffix = qs.toString() ? `?${qs}` : "";
  return apiFetch(`/api/bookings/${id}/documents/${art}${suffix}`, { method: "POST" });
}

export type StornoVorberechnung = {
  regel: string;
  faktor: number;
  basis: number;
  betrag: number;
  storno_gebuehr: number | null;
};

export function fetchStornoVorberechnung(id: number): Promise<StornoVorberechnung> {
  return apiFetch<StornoVorberechnung>(`/api/bookings/${id}/storno-vorberechnung`);
}

export function deleteAblageFile(filePath: string, bookingId: number): Promise<{ detail: string }> {
  return apiFetch(`/api/ablage/${encodeURIComponent(filePath)}?booking_id=${bookingId}`, {
    method: "DELETE",
  });
}

/**
 * Lädt eine Ablage-Datei per fetch als Blob (Auth läuft über das Session-Cookie).
 */
export async function fetchAblageBlob(filePath: string): Promise<Blob> {
  // {file_path:path} in FastAPI erwartet echte Slashes im URL-Pfad, keine %2F.
  // Führender Slash absoluter Pfade muss erhalten bleiben (sonst wird der Pfad
  // serverseitig gegen das CWD aufgelöst und liegt nicht mehr unter der Ablage).
  const isAbsolute = filePath.startsWith("/");
  const segments = filePath.replace(/^\//, "").split("/").map(encodeURIComponent);
  const joined = segments.join("/");
  const url = `/api/ablage/${isAbsolute ? "/" : ""}${joined}/download`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Download fehlgeschlagen (${res.status})`);
  return await res.blob();
}

/**
 * Lädt eine Ablage-Datei und startet den Browser-Download.
 */
export async function downloadAblageFile(filePath: string, fileName: string): Promise<void> {
  const blob = await fetchAblageBlob(filePath);
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}


/**
 * Erzeugt die Konus-XML-Meldung serverseitig und startet den Browser-Download
 * mit dem vom Server vergebenen Dateinamen.
 */
/**
 * Lädt eine Meldeschein-PDF zur Buchung hoch.
 */
export function uploadMeldeschein(
  bookingId: number,
  file: File,
): Promise<{ detail: string; path: string }> {
  const fd = new FormData();
  fd.append("datei", file);
  return apiFetch<{ detail: string; path: string }>(
    `/api/bookings/${bookingId}/meldeschein`,
    { method: "POST", body: fd },
  );
}


export async function downloadKonusXml(bookingId: number): Promise<string> {
  const res = await fetch(`/api/bookings/${bookingId}/konus-xml`, {
    method: "POST",
  });
  if (!res.ok) {
    let detail = `Fehler (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) detail = String(j.detail);
    } catch { /* nicht-JSON-Antwort, Default-Text behalten */ }
    throw new Error(detail);
  }
  const cd = res.headers.get("Content-Disposition") || "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const fileName = m?.[1] || `Konus_Meldung_${bookingId}.xml`;
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
  return fileName;
}


/**
 * PUT /api/bookings/{id} — schickt das vollständige Booking als BookingIn-Form.
 * Da Pydantic-BookingIn alle Felder erwartet, muss der Caller Patch + aktuelle
 * Buchung selbst mergen (siehe ``buildBookingInFromBooking``).
 */
export function updateBooking(
  id: number,
  body: Record<string, unknown>,
  force = false,
): Promise<Booking> {
  return apiFetch<Booking>(`/api/bookings/${id}${force ? "?force=true" : ""}`, {
    method: "PUT",
    body,
  });
}

/**
 * Baut aus einer Booking + einem Patch das BookingIn-Objekt fürs PUT.
 * BookingIn erwartet die meisten Felder als Strings/Numbers, nicht null —
 * wir liefern sinnvolle Defaults (leerstring / 0).
 */
export function buildBookingInFromBooking(
  bk: Booking,
  patch: BookingPatch = {},
): Record<string, unknown> {
  const merge = <T,>(p: T | undefined, fallback: T): T => (p !== undefined ? p : fallback);
  return {
    gruppenname: merge(patch.gruppenname, bk.gruppenname || ""),
    anrede: merge(patch.anrede, bk.anrede || ""),
    vorname: merge(patch.vorname, bk.vorname || ""),
    nachname: merge(patch.nachname, bk.nachname || ""),
    gastname: merge(patch.gastname, bk.gastname || ""),
    email: merge(patch.email, bk.email),
    einheiten_ids: merge (patch.einheiten_ids, bk.einheiten_ids),
    check_in: merge(patch.check_in, bk.check_in),
    check_out: merge(patch.check_out, bk.check_out),
    check_in_time: merge(patch.check_in_time, bk.check_in_time || "15:00"),
    check_out_time: merge(patch.check_out_time, bk.check_out_time || "10:00"),
    personen_ab_16: merge(patch.personen_ab_16, bk.personen_ab_16 ?? 0),
    kinder_6_bis_15: merge(patch.kinder_6_bis_15, bk.kinder_6_bis_15 ?? 0),
    kinder_unter_6: merge(patch.kinder_unter_6, bk.kinder_unter_6 ?? 0),
    nachreinigung_eur: merge(patch.nachreinigung_eur, bk.nachreinigung_eur ?? 0),
    status: merge(patch.status, bk.status),
    bemerkung: merge(patch.bemerkung, bk.bemerkung || ""),
    telefon: merge(patch.telefon, bk.telefon || ""),
    gruppenart: merge(patch.gruppenart, bk.gruppenart || ""),
    saison: merge(patch.saison, bk.saison || "Sommer"),
    adresse_strasse: merge(patch.adresse_strasse, bk.adresse_strasse || ""),
    adresse_plz: merge(patch.adresse_plz, bk.adresse_plz || ""),
    adresse_ort: merge(patch.adresse_ort, bk.adresse_ort || ""),
    adresse_land: merge(patch.adresse_land, bk.adresse_land || "DE"),
    buchungsdatum: merge(patch.buchungsdatum, bk.buchungsdatum || ""),
    rechnung_bemerkung: merge(patch.rechnung_bemerkung, bk.rechnung_bemerkung || ""),
    rechnung_zahlungsziel: merge(patch.rechnung_zahlungsziel, bk.rechnung_zahlungsziel || ""),
    rechnung_fehlteile: merge(patch.rechnung_fehlteile, bk.rechnung_fehlteile || ""),
    kurtaxe_betrag: merge(patch.kurtaxe_betrag, bk.kurtaxe_betrag ?? null),
    anzahlung_betrag: merge(patch.anzahlung_betrag, bk.anzahlung_betrag ?? 100),
    anzahlung_erhalten: merge(patch.anzahlung_erhalten, bk.anzahlung_erhalten ?? false),
    schluesselcode_eg: merge(patch.schluesselcode_eg, bk.schluesselcode_eg ?? null),
    schluesselcode_og: merge(patch.schluesselcode_og, bk.schluesselcode_og ?? null),
    schluesselcode_dg: merge(patch.schluesselcode_dg, bk.schluesselcode_dg ?? null),
    schluesselcode_lz: merge(patch.schluesselcode_lz, bk.schluesselcode_lz ?? null),
    storniert_am: merge(patch.storniert_am, bk.storniert_am ?? null),
    bestaetigung_extern_versandt: merge(patch.bestaetigung_extern_versandt, bk.bestaetigung_extern_versandt ?? false),
    bestaetigung_extern_versandt_am: merge(patch.bestaetigung_extern_versandt_am, bk.bestaetigung_extern_versandt_am ?? null),
    infos_gruppe_extern_versandt: merge(patch.infos_gruppe_extern_versandt, bk.infos_gruppe_extern_versandt ?? false),
    infos_gruppe_extern_versandt_am: merge(patch.infos_gruppe_extern_versandt_am, bk.infos_gruppe_extern_versandt_am ?? null),
    konus_link_versandt: merge(patch.konus_link_versandt, bk.konus_link_versandt ?? false),
    konus_link_versandt_am: merge(patch.konus_link_versandt_am, bk.konus_link_versandt_am ?? null),
    konus_daten_eingetragen_am: merge(patch.konus_daten_eingetragen_am, bk.konus_daten_eingetragen_am ?? null),
    konus_daten_eingetragen: merge(patch.konus_daten_eingetragen, bk.konus_daten_eingetragen ?? false),
    rechnung_versandt_am: merge(patch.rechnung_versandt_am, bk.rechnung_versandt_am ?? null),
    bezahlt_am: merge(patch.bezahlt_am, bk.bezahlt_am ?? null),
    anzahlung_erhalten_am: merge(patch.anzahlung_erhalten_am, bk.anzahlung_erhalten_am ?? null),
    gruppenleiter_anrede: merge(patch.gruppenleiter_anrede, bk.gruppenleiter_anrede ?? null),
    gruppenleiter_geburtsdatum: merge(patch.gruppenleiter_geburtsdatum, bk.gruppenleiter_geburtsdatum ?? null),
    gruppenleiter_vorname: merge(patch.gruppenleiter_vorname, bk.gruppenleiter_vorname ?? null),
    gruppenleiter_nachname: merge(patch.gruppenleiter_nachname, bk.gruppenleiter_nachname ?? null),
    gruppenleiter_adresse_strasse: merge(patch.gruppenleiter_adresse_strasse, bk.gruppenleiter_adresse_strasse ?? null),
    gruppenleiter_adresse_hausnummer: merge(patch.gruppenleiter_adresse_hausnummer, bk.gruppenleiter_adresse_hausnummer ?? null),
    gruppenleiter_adresse_plz: merge(patch.gruppenleiter_adresse_plz, bk.gruppenleiter_adresse_plz ?? null),
    gruppenleiter_adresse_ort: merge(patch.gruppenleiter_adresse_ort, bk.gruppenleiter_adresse_ort ?? null),
    gruppenleiter_adresse_land: merge(patch.gruppenleiter_adresse_land, bk.gruppenleiter_adresse_land ?? null),
    meldescheinnummer: merge(patch.meldescheinnummer, bk.meldescheinnummer ?? null),
    rechnung_rabatt_prozent: merge(patch.rechnung_rabatt_prozent, bk.rechnung_rabatt_prozent ?? 0),
    rechnung_rabatt_grund: merge(patch.rechnung_rabatt_grund, bk.rechnung_rabatt_grund ?? ""),
  };
}

export async function downloadEmailDraft(bookingId: number, art: string): Promise<void> {
  const res = await fetch(`/api/bookings/${bookingId}/email_draft/${art}`);
  if (!res.ok) {
    let detail = `E-Mail-Entwurf fehlgeschlagen (HTTP ${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch { /* kein JSON */ }
    throw new Error(detail);
  }
  const cd = res.headers.get("Content-Disposition") || "";
  const fnMatch = cd.match(/filename="([^"]+)"/);
  const filename = fnMatch ? fnMatch[1] : `email_entwurf_${bookingId}.eml`;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}


export type PreisBerechnung = {
  ok: boolean;
  tarif_gesamt: number;
  einheiten_details: { name: string; preis: number; beschreibung: string }[];
};

export function fetchPreisBerechnung(bookingId: number): Promise<PreisBerechnung> {
  return apiFetch<PreisBerechnung>(`/api/bookings/${bookingId}/preis-berechnung`);
}

export function postSendKonusLink(id: number): Promise<{ detail: string; konus_link_versandt_am: string }> {
  return apiFetch(`/api/bookings/${id}/send-konus-link`, { method: "POST" });
}

export async function downloadKonusLinkEmailDraft(bookingId: number): Promise<void> {
  const res = await fetch(`/api/bookings/${bookingId}/konus-link/email-draft`);
  if (!res.ok) {
    let detail = `E-Mail-Entwurf fehlgeschlagen (HTTP ${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) detail = j.detail;
    } catch { /* kein JSON */ }
    throw new Error(detail);
  }
  const cd = res.headers.get("Content-Disposition") || "";
  const fnMatch = cd.match(/filename="([^"]+)"/);
  const filename = fnMatch ? fnMatch[1] : `konus_link_${bookingId}.eml`;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export type EmailDraft = {
  empfaenger: string;
  betreff: string;
  text: string;
  pdf_dateiname: string | null;
};

export function getEmailJsonDraft(bookingId: number, art: string): Promise<EmailDraft> {
  return apiFetch<EmailDraft>(`/api/bookings/${bookingId}/emails/${art}/draft-json`);
}

export function sendEmail(
  bookingId: number,
  art: string,
  payload: { empfaenger: string; betreff: string; text: string },
): Promise<{ detail: string }> {
  return apiFetch(`/api/bookings/${bookingId}/emails/${art}/send`, { method: "POST", body: payload });
}

/** Rendert die fertige CI-HTML-Mail aus dem editierten Text (für die Vorschau). */
export function getEmailPreviewHtml(bookingId: number, art: string, text: string): Promise<string> {
  // Antwort ist text/html; apiFetch liefert für Nicht-JSON automatisch den Text.
  return apiFetch<string>(`/api/bookings/${bookingId}/emails/${art}/preview-html`, {
    method: "POST",
    body: { text },
  });
}

export type RechnungPreviewParams = {
  rechnung_bemerkung?: string;
  rechnung_zahlungsziel?: string;
  nachreinigung_eur?: number;
  anzahlung_erhalten?: boolean;
  anzahlung_betrag?: number;
  rechnungsdatum?: string;
  rechnung_rabatt_prozent?: number;
  rechnung_fehlteile?: string;
  saison?: string;
  gruppenart?: string;
};

export type RechnungPositionItem = {
  bezeichnung: string;
  detail: string | null;
  menge_text: string;
  einzelpreis_text: string;
  gesamt: number;
  kind: "unterkunft" | "kurtaxe" | "nachreinigung" | "rabatt" | "anzahlung" | "storno" | "hinweis";
};

export type RechnungPositionenResponse = {
  status: string;
  hinweis: string | null;
  positionen: RechnungPositionItem[];
  zwischensumme: number;
  rabatt_prozent: number;
  rabatt_betrag: number;
  anzahlung_abzug: number;
  gesamtbetrag: number;
};

export type RechnungPreviewResponse = {
  html: string;
  positionen: RechnungPositionenResponse;
};

/**
 * Holt das gefüllte HTML der Rechnungs-Vorschau für die Live-Preview im iframe.
 * Mutiert nichts — Werte aus ``params`` sind nur Overrides für diesen Render.
 */
export async function fetchRechnungPreview(
  bookingId: number,
  params: RechnungPreviewParams = {},
  signal?: AbortSignal,
): Promise<RechnungPreviewResponse> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    qs.set(k, typeof v === "boolean" ? (v ? "1" : "0") : String(v));
  }
  const url = `/api/bookings/${bookingId}/rechnung/preview${qs.toString() ? "?" + qs : ""}`;
  const res = await fetch(url, { signal });
  if (!res.ok) {
    let detail = `Vorschau fehlgeschlagen (HTTP ${res.status})`;
    try {
      const j = await res.json();
      if (j && typeof j.detail === "string") detail = j.detail;
    } catch {
      /* keine JSON-Antwort */
    }
    throw new Error(detail);
  }
  return (await res.json()) as RechnungPreviewResponse;
}
